import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { MagnifyingGlassIcon, PencilSquareIcon, PlusIcon, TrashIcon } from '@heroicons/react/24/outline';
import technicalMaintenanceApi, { EQUIPMENT_CATEGORIES } from '../../api/technicalMaintenanceApi';
import DataTable from '../../components/ui/DataTable';
import IconActionButton from '../../components/ui/IconActionButton';
import TechnicalMaintenanceFormModal from './TechnicalMaintenanceFormModal';
import { TechnicalMaintenancePageShell } from './technicalMaintenanceUi';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Label } from '../../components/ui/Label';
import { Select } from '../../components/ui/Select';
import { useAuth } from '../../contexts/AuthContext';

const initialForm = {
  category: 'machine_list',
  name: '',
  equipment_code: '',
  area: '',
  location_detail: '',
  temperature: '',
  brand: '',
  model_name: '',
  serial_number: '',
  maintenance_interval_days: '',
  warranty_expiry_date: '',
  notes: '',
  is_active: true,
};

const defaultFilters = {
  search: '',
  area: '',
  is_active: '',
  category: '',
};

const getEquipmentSearchText = (row) => [
  row.name,
  row.equipment_code,
  row.area,
  row.location_detail,
  row.category_label,
  row.brand,
  row.model_name,
  row.serial_number,
  row.notes,
].filter(Boolean).join(' ').toLowerCase();

const matchesEquipmentFilters = (row, activeFilters) => {
  if (activeFilters.category && row.category !== activeFilters.category) {
    return false;
  }
  if (activeFilters.area && row.area !== activeFilters.area) {
    return false;
  }
  if (activeFilters.is_active === 'true' && !row.is_active) {
    return false;
  }
  if (activeFilters.is_active === 'false' && row.is_active !== false) {
    return false;
  }
  const searchTerm = activeFilters.search.trim().toLowerCase();
  if (searchTerm && !getEquipmentSearchText(row).includes(searchTerm)) {
    return false;
  }
  return true;
};

const TechnicalMaintenanceEquipmentPage = () => {
  const { getFullName } = useAuth();
  const navigate = useNavigate();
  const [allRows, setAllRows] = useState([]);
  const [rows, setRows] = useState([]);
  const [areas, setAreas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [infoMessage, setInfoMessage] = useState(null);
  const [showFilters, setShowFilters] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingRow, setEditingRow] = useState(null);
  const [formData, setFormData] = useState(initialForm);
  const [filters, setFilters] = useState(defaultFilters);
  const [draftFilters, setDraftFilters] = useState(defaultFilters);

  const loadData = async () => {
    setLoading(true);
    setError(null);
    try {
      const [equipmentResponse, optionsResponse] = await Promise.all([
        technicalMaintenanceApi.getEquipment(),
        technicalMaintenanceApi.getEquipmentFilterOptions(),
      ]);
      setAllRows(equipmentResponse?.data || []);
      setAreas(optionsResponse?.areas || optionsResponse?.data?.areas || []);
    } catch (err) {
      setError(err.message || 'Ekipman listesi yüklenemedi.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    setRows(allRows.filter((row) => matchesEquipmentFilters(row, filters)));
  }, [allRows, filters]);

  const areaOptions = useMemo(() => {
    const values = new Set(areas);
    allRows.forEach((row) => {
      if (row.area) values.add(row.area);
    });
    return [...values].sort((a, b) => a.localeCompare(b, 'tr'));
  }, [areas, allRows]);

  const showAllCategories = !filters.category;

  const openCreate = () => {
    setEditingRow(null);
    setFormData({
      ...initialForm,
      category: filters.category || 'machine_list',
    });
    setIsModalOpen(true);
  };

  const openEdit = (row) => {
    setEditingRow(row);
    setFormData({
      category: row.category,
      name: row.name || '',
      equipment_code: row.equipment_code || '',
      area: row.area || '',
      location_detail: row.location_detail || '',
      temperature: row.temperature || '',
      brand: row.brand || '',
      model_name: row.model_name || '',
      serial_number: row.serial_number || '',
      maintenance_interval_days: row.maintenance_interval_days ?? '',
      warranty_expiry_date: row.warranty_expiry_date || '',
      notes: row.notes || '',
      is_active: row.is_active !== false,
    });
    setIsModalOpen(true);
  };

  const handleSave = async () => {
    if (!formData.name.trim()) {
      setError('Ad / Tip alanı zorunludur.');
      return;
    }
    setSaving(true);
    setError(null);
    setInfoMessage(null);
    try {
      const becameInactive = Boolean(editingRow && !formData.is_active && editingRow.is_active !== false);
      const payload = {
        ...formData,
        maintenance_interval_days: formData.maintenance_interval_days === ''
          ? null
          : Number(formData.maintenance_interval_days),
        warranty_expiry_date: formData.warranty_expiry_date || null,
      };
      if (editingRow) {
        await technicalMaintenanceApi.updateEquipment(editingRow.id, payload);
      } else {
        await technicalMaintenanceApi.createEquipment(payload);
      }
      setIsModalOpen(false);

      let nextFilters = filters;
      if (becameInactive && filters.is_active === 'true') {
        nextFilters = { ...filters, is_active: '' };
        setFilters(nextFilters);
        setDraftFilters(nextFilters);
        setInfoMessage(
          `"${formData.name}" pasif olarak kaydedildi. Kayıt silinmedi; listede "Pasif" olarak görünür. Tekrar aktif yapmak için düzenleyip "Aktif" işaretleyin.`
        );
      }

      await loadData();
    } catch (err) {
      setError(err.message || 'Kayıt kaydedilemedi.');
    } finally {
      setSaving(false);
    }
  };

  const handleActiveChange = (checked) => {
    if (!checked && editingRow?.is_active !== false) {
      const confirmed = window.confirm(
        'Bu ekipman pasif yapılacak. Varsayılan listeden gizlenir ama silinmez. Pasif kayıtları görmek için filtrede Durum → Pasif veya Tümü seçin. Devam edilsin mi?'
      );
      if (!confirmed) return;
    }
    setFormData({ ...formData, is_active: checked });
  };

  const handleDelete = async (row) => {
    if (!window.confirm(`"${row.name}" silinsin mi?`)) return;
    try {
      await technicalMaintenanceApi.deleteEquipment(row.id);
      await loadData();
    } catch (err) {
      setError(err.message || 'Kayıt silinemedi.');
    }
  };

  const handleSearch = () => {
    setError(null);
    setFilters({ ...draftFilters });
  };

  const handleClearFilters = () => {
    setDraftFilters(defaultFilters);
    setFilters(defaultFilters);
  };

  const columns = useMemo(() => [
    {
      header: 'Ekipman',
      accessorKey: 'name',
      cell: ({ row }) => (
        <div>
          <div className="font-medium text-gray-900 dark:text-white">{row.original.name}</div>
          <div className="text-xs text-gray-500">
            {[row.original.equipment_code, row.original.area].filter(Boolean).join(' · ') || '—'}
          </div>
        </div>
      ),
    },
    ...(showAllCategories ? [{
      header: 'Kategori',
      accessorKey: 'category_label',
      cell: ({ getValue }) => (
        <span className="tm-badge area">{getValue()}</span>
      ),
    }] : []),
    {
      header: 'Konum',
      accessorKey: 'location_detail',
      cell: ({ getValue }) => getValue() || '—',
    },
    {
      header: 'Durum',
      accessorKey: 'is_active',
      cell: ({ row }) => (
        <span className={`tm-badge ${row.original.is_active ? 'ok' : 'overdue'}`}>
          {row.original.is_active ? 'Aktif' : 'Pasif'}
        </span>
      ),
    },
    {
      header: '',
      accessorKey: 'actions',
      size: 80,
      cell: ({ row }) => (
        <div className="flex gap-1" onClick={(e) => e.stopPropagation()}>
          <IconActionButton icon={PencilSquareIcon} title="Düzenle" onClick={() => openEdit(row.original)} />
          <IconActionButton icon={TrashIcon} title="Sil" variant="danger" onClick={() => handleDelete(row.original)} />
        </div>
      ),
    },
  ], [showAllCategories]);

  return (
    <TechnicalMaintenancePageShell
      title="Ekipman Envanteri"
      subtitle="Makine, soğuk oda, dolap ve buz makinesi kayıtları. Satıra tıklayarak bakım geçmişini görün."
      actions={(
        <Button onClick={openCreate}>
          <PlusIcon className="h-4 w-4 mr-1 inline" />
          Yeni Ekipman
        </Button>
      )}
    >
      <div className="tm-summary-bar">
        <span className="tm-summary-pill">Gösterilen <strong>{rows.length}</strong></span>
        <span className="tm-summary-pill">Aktif <strong>{rows.filter((r) => r.is_active).length}</strong></span>
      </div>

      <div className="tm-toolbar mb-3">
        <div className="tm-segmented">
          <button
            type="button"
            className={`tm-segment ${!draftFilters.category ? 'active' : ''}`}
            onClick={() => {
              const next = { ...draftFilters, category: '' };
              setDraftFilters(next);
              setFilters(next);
            }}
          >
            Tümü
          </button>
          {EQUIPMENT_CATEGORIES.map((item) => (
            <button
              key={item.value}
              type="button"
              className={`tm-segment ${draftFilters.category === item.value ? 'active' : ''}`}
              onClick={() => {
                const next = { ...draftFilters, category: item.value };
                setDraftFilters(next);
                setFilters(next);
              }}
            >
              {item.label}
            </button>
          ))}
        </div>
      </div>

      <div className="tm-inline-filters">
        <div className="field wide">
          <Label>Ara</Label>
          <div className="relative">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
            <Input
              className="pl-9"
              value={draftFilters.search}
              onChange={(e) => setDraftFilters({ ...draftFilters, search: e.target.value })}
              onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
              placeholder="Ad, kod, alan..."
            />
          </div>
        </div>
        <Button variant="secondary" onClick={() => setShowFilters((v) => !v)}>
          {showFilters ? 'Filtreleri Gizle' : 'Detaylı Filtre'}
        </Button>
        <Button onClick={handleSearch}>Uygula</Button>
      </div>

      {showFilters && (
        <div className="tm-inline-filters">
          <div className="field wide">
            <Label>Alan / Bölüm</Label>
            <Select value={draftFilters.area} onChange={(e) => setDraftFilters({ ...draftFilters, area: e.target.value })}>
              <option value="">Tümü</option>
              {areaOptions.map((area) => (
                <option key={area} value={area}>{area}</option>
              ))}
            </Select>
          </div>
          <div className="field">
            <Label>Durum</Label>
            <Select value={draftFilters.is_active} onChange={(e) => setDraftFilters({ ...draftFilters, is_active: e.target.value })}>
              <option value="">Tümü</option>
              <option value="true">Aktif</option>
              <option value="false">Pasif</option>
            </Select>
          </div>
          <Button variant="secondary" onClick={handleClearFilters}>Temizle</Button>
        </div>
      )}

      {error && (
        <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-red-900 dark:bg-red-950/40 dark:text-red-200">
          {error}
        </div>
      )}
      {infoMessage && (
        <div className="mb-4 rounded-lg border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-900 dark:border-blue-800 dark:bg-blue-950/40 dark:text-blue-100">
          {infoMessage}
        </div>
      )}

      <div className="tm-dash-card" style={{ padding: 0, overflow: 'hidden' }}>
        <DataTable
          columns={columns}
          data={rows}
          isLoading={loading}
          showSearch={false}
          onRowClick={(row) => navigate(`/internal-audit/technical-maintenance/equipment/${row.id}`)}
        />
      </div>

      <TechnicalMaintenanceFormModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title={editingRow ? 'Ekipman Düzenle' : 'Yeni Ekipman'}
        footer={(
          <>
            <Button variant="secondary" onClick={() => setIsModalOpen(false)}>İptal</Button>
            <Button onClick={handleSave} disabled={saving}>{saving ? 'Kaydediliyor...' : 'Kaydet'}</Button>
          </>
        )}
      >
        <div className="space-y-4">
          <div>
            <Label>Kategori</Label>
            <Select
              value={formData.category}
              onChange={(e) => setFormData({ ...formData, category: e.target.value })}
            >
              {EQUIPMENT_CATEGORIES.map((item) => (
                <option key={item.value} value={item.value}>{item.label}</option>
              ))}
            </Select>
          </div>
          <div>
            <Label>Ad / Tip</Label>
            <Input value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} />
          </div>
          <div>
            <Label>Kod</Label>
            <Input value={formData.equipment_code} onChange={(e) => setFormData({ ...formData, equipment_code: e.target.value })} />
          </div>
          <div>
            <Label>Alan</Label>
            <Input value={formData.area} onChange={(e) => setFormData({ ...formData, area: e.target.value })} />
          </div>
          <div>
            <Label>Konum Detayı</Label>
            <Input value={formData.location_detail} onChange={(e) => setFormData({ ...formData, location_detail: e.target.value })} />
          </div>
          {formData.category === 'cold_room' && (
            <div>
              <Label>Sıcaklık</Label>
              <Input value={formData.temperature} onChange={(e) => setFormData({ ...formData, temperature: e.target.value })} />
            </div>
          )}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label>Marka</Label>
              <Input value={formData.brand} onChange={(e) => setFormData({ ...formData, brand: e.target.value })} />
            </div>
            <div>
              <Label>Model</Label>
              <Input value={formData.model_name} onChange={(e) => setFormData({ ...formData, model_name: e.target.value })} />
            </div>
            <div>
              <Label>Seri No</Label>
              <Input value={formData.serial_number} onChange={(e) => setFormData({ ...formData, serial_number: e.target.value })} />
            </div>
            <div>
              <Label>Periyodik Bakım Aralığı (gün)</Label>
              <Input
                type="number"
                min="1"
                placeholder="Örn: 90"
                value={formData.maintenance_interval_days}
                onChange={(e) => setFormData({ ...formData, maintenance_interval_days: e.target.value })}
              />
            </div>
            <div>
              <Label>Garanti Bitiş Tarihi</Label>
              <Input
                type="date"
                value={formData.warranty_expiry_date}
                onChange={(e) => setFormData({ ...formData, warranty_expiry_date: e.target.value })}
              />
            </div>
          </div>
          <div>
            <Label>Not</Label>
            <Input value={formData.notes} onChange={(e) => setFormData({ ...formData, notes: e.target.value })} />
          </div>
          <div>
            <Label>Ekleyen</Label>
            <Input
              value={editingRow?.created_by_name || getFullName() || '—'}
              readOnly
              disabled
              className="bg-gray-50 dark:bg-gray-800/60 cursor-not-allowed"
            />
          </div>
          <label className="flex items-start gap-2 text-sm text-gray-700 dark:text-gray-200">
            <input
              type="checkbox"
              className="mt-0.5"
              checked={formData.is_active}
              onChange={(e) => handleActiveChange(e.target.checked)}
            />
            <span>
              Aktif
              <span className="mt-0.5 block text-xs text-gray-500 dark:text-gray-400">
                Pasif yapılırsa kayıt silinmez; varsayılan listeden gizlenir.
              </span>
            </span>
          </label>
        </div>
      </TechnicalMaintenanceFormModal>
    </TechnicalMaintenancePageShell>
  );
};

export default TechnicalMaintenanceEquipmentPage;
